import { useEffect, useContext, useRef, useCallback } from "react";
import AppContext from "../components/AppContext";
import { isTokenExpired } from "./auth";

/**
 * คำนวณเวลาถัดไปพร้อม Jitter +/- 10%
 * @param {string|number} maxInterval - เวลาฐาน (ms)
 */
function getRandomInterval(maxInterval) {
  const DEFAULT_MS = 120000; // 2 mins
  const MIN_MS = 2000; // 2 secs

  const baseMs = parseInt(maxInterval, 10) || DEFAULT_MS;
  const jitter = (Math.random() * 0.2 - 0.1) * baseMs;

  return Math.max(MIN_MS, baseMs + jitter);
}

// Map สำหรับ Page ที่อนุญาตให้รัน Auto Update (สามารถเพิ่ม Page ที่ต้องการอนุญาตให้รัน Auto Update)
// หมายเหตุ: ตรวจสอบให้แน่ใจว่า page ที่อนุญาติให้รัน Auto Update มีฟังชั่น onUpdateAuto ที่ทำงานถูกต้อง
const ALLOWED_ROUTES = [
  "deposit-transaction",
  "withdraw-transaction",
  "dashboard",
  "bank-account",
  "verifyslip-transactions",
  "approve-topup",
  "verify-customer",
];

function UpdateDataByRoute({ onUpdateAuto }) {
  const timeoutIdRef = useRef(null);
  const { activeKey1, merchantId, setMerchantId } = useContext(AppContext);
  // --- ส่วนที่แก้ไข: การซิงค์ Merchant ID ---
  useEffect(() => {
    const mID = localStorage.getItem("selectedID");
    const storedId = mID ? parseInt(mID, 10) : null;

    // ถ้าใน AppContext มีค่า แต่ใน LocalStorage ไม่ตรง หรือไม่มีค่า
    if (merchantId && merchantId !== storedId) {
      localStorage.setItem("selectedID", merchantId);
    }
    // ถ้าใน AppContext ไม่มีค่า แต่ใน LocalStorage มี (เช่น หลัง Refresh หน้าจอ)
    else if (!merchantId && storedId) {
      setMerchantId(storedId);
    }
  }, [merchantId, setMerchantId]);

  // เก็บ callback ล่าสุดไว้เสมอโดยไม่ทำให้ useCallback ทำงานใหม่
  const onUpdateRef = useRef(onUpdateAuto);
  useEffect(() => {
    onUpdateRef.current = onUpdateAuto;
  }, [onUpdateAuto]);

  const scheduleNextUpdate = useCallback(() => {
    // ล้าง Timer เก่าก่อนเริ่มรอบใหม่
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

    const sessionInterval = sessionStorage.getItem("Interval") || "120000";
    const nextTime = getRandomInterval(sessionInterval);

    timeoutIdRef.current = setTimeout(async () => {
      // ตรวจสอบสถานะ Token
      if (isTokenExpired()) {
        console.warn("[AutoUpdate] Token expired, waiting for next cycle...");
        scheduleNextUpdate();
        return;
      }

      // ตรวจสอบว่า Route ปัจจุบันอยู่ในรายการที่ต้องอัปเดตหรือไม่
      try {
        if (
          ALLOWED_ROUTES.includes(activeKey1) &&
          typeof onUpdateRef.current === "function"
        ) {
          await onUpdateRef.current();
        }
      } catch (error) {
        console.error("[AutoUpdate] Error during update:", error);
      } finally {
        // อัปเดตรอบถัดไปเสมอ (Recursive loop)
        scheduleNextUpdate();
      }
    }, nextTime);
  }, [activeKey1]); // ทำงานใหม่เฉพาะตอนเปลี่ยน Route

  useEffect(() => {
    scheduleNextUpdate();
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [scheduleNextUpdate]);

  return null;
}

export default UpdateDataByRoute;
